import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Link
} from '@mui/material'
import {
  Settings as SettingsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Link as LinkIcon
} from '@mui/icons-material'
import { apiClient } from '../../services/api'
import { poolwerxColors } from '../../theme/colors'
import { formatToLongDate } from '../../utils/textUtils'
import { getSystemIcon } from '../../components/SystemIcons'
import { openXeroLink, openLightspeedLink } from '../../utils/externalLinks'
import {
  generateXeroMainUrl,
  generateXeroAppUrl,
  generateLightspeedMainUrl,
  generateLightspeedTokensUrl,
  generateAdminFpUrl
} from '../../utils/urls'
import XeroReauthorizeDialog from '../../components/XeroReauthorizeDialog'
import {
  useReportFilters,
  ReportFilterCard,
  FilterKey,
} from '../../components/shared/filters'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import BaseDataTable, { TableColumn } from '../../components/shared/BaseDataTable'
import { useDataFetching } from '../../hooks/useDataFetching'

interface SystemConnection {
  id: number
  fp_id: number
  fp_code: string
  franchise_name: string
  country?: string
  fp_platform_status?: string
  system_name: string
  connection_id: string
  connection_status: string
  last_tested_at: string | null
  last_sync_at: string | null
  error_message: string | null
  metadata?: {
    tenant_name?: string
    tenant_id?: string
    store_code?: string
    api_account_id?: string
    last_connected?: string
    api_url?: string
    environment?: string
    short_code?: string
    connection_method?: string
    scopes?: string[]
  }
  created_at: string
  updated_at: string
}

type SortField = 'fp_code' | 'franchise_name' | 'system_name' | 'connection_status' | 'last_tested_at' | 'last_sync_at'
type SortDirection = 'asc' | 'desc'

const ENABLED_FILTERS: FilterKey[] = ['search', 'system', 'connectionStatus']

const SystemConnections: React.FC = () => {
  const navigate = useNavigate()

  // Fetch all connections once; client-side filter/sort/paginate below
  const {
    data: connectionsData,
    loading,
    error,
    refetch: fetchConnections,
  } = useDataFetching<SystemConnection[]>({
    fetchFn: async () => {
      const response = await apiClient.get('/admin/system-connections')
      return response.data.connections || []
    },
  })
  const connections = connectionsData || []

  // Shared filter hook for all filters
  const {
    filters: sharedFilters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
  } = useReportFilters({
    enabledFilters: ['search', 'system', 'connectionStatus'] as FilterKey[],
    syncToUrl: true,
  })

  const [sortField, setSortField] = useState<SortField>('system_name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Pagination state
  const [page, setPage] = useState(1)
  const [rowsPerPage] = useState(25)

  // Xero reauthorization state
  const [reauthorizeDialog, setReauthorizeDialog] = useState<{
    open: boolean
    fpCode: string
    franchiseName: string
    tenantName?: string
    loading: boolean
  }>({
    open: false,
    fpCode: '',
    franchiseName: '',
    tenantName: '',
    loading: false
  })

  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [sharedFilters])

  // Filter and sort connections
  const filteredAndSortedConnections = useMemo(() => {
    let filtered = connections.filter(connection => {
      // Search filter (FP code, franchise name, or system)
      const searchMatch = !sharedFilters.search ||
        connection.fp_code.toLowerCase().includes(sharedFilters.search.toLowerCase()) ||
        connection.franchise_name.toLowerCase().includes(sharedFilters.search.toLowerCase()) ||
        connection.system_name.toLowerCase().includes(sharedFilters.search.toLowerCase())

      // System filter
      const systemMatch = !sharedFilters.system || connection.system_name === sharedFilters.system

      // Connection status filter
      const statusMatch = !sharedFilters.connectionStatus || connection.connection_status === sharedFilters.connectionStatus

      return searchMatch && systemMatch && statusMatch
    })

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortField]
      let bValue: any = b[sortField]

      // Handle null values for dates
      if (sortField === 'last_tested_at' || sortField === 'last_sync_at') {
        if (!aValue && !bValue) return 0
        if (!aValue) return sortDirection === 'asc' ? 1 : -1
        if (!bValue) return sortDirection === 'asc' ? -1 : 1
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [connections, sharedFilters, sortField, sortDirection])

  // Paginate the filtered results
  const paginatedConnections = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return filteredAndSortedConnections.slice(startIndex, endIndex)
  }, [filteredAndSortedConnections, page, rowsPerPage])

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field as SortField)
    setSortDirection(direction)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleManageConnection = (connection: SystemConnection) => {
    navigate(generateAdminFpUrl(connection.fp_id, 'connections'))
  }

  // Handle opening main system application (system column clicks)
  const handleSystemMainAppClick = (connection: SystemConnection) => {
    if (connection.system_name === 'xero' && connection.metadata?.short_code) {
      const xeroUrl = generateXeroMainUrl(connection.metadata.short_code)
      openXeroLink(xeroUrl, 'Xero Application')
    } else if (connection.system_name === 'lightspeed' && (connection.metadata?.store_code || connection.connection_id)) {
      const subdomain = connection.metadata?.store_code || connection.connection_id
      const lightspeedUrl = generateLightspeedMainUrl(subdomain)
      openLightspeedLink(lightspeedUrl, 'Lightspeed Application')
    }
  }

  // Handle opening system integrations management (chain button clicks)
  const handleSystemIntegrationClick = (connection: SystemConnection) => {
    if (connection.system_name === 'xero' && connection.metadata?.short_code) {
      const xeroUrl = generateXeroAppUrl(connection.metadata.short_code)
      openXeroLink(xeroUrl, 'Xero Organization')
    } else if (connection.system_name === 'lightspeed' && (connection.metadata?.store_code || connection.connection_id)) {
      const subdomain = connection.metadata?.store_code || connection.connection_id
      const lightspeedUrl = generateLightspeedTokensUrl(subdomain)
      openLightspeedLink(lightspeedUrl, 'Lightspeed Personal Tokens')
    }
  }

  // Handle Xero status chip click for re-authorization
  const handleStatusClick = (connection: SystemConnection) => {
    if (connection.system_name === 'xero' &&
        (connection.connection_status === 'error' ||
         connection.connection_status === 'reauthorization_required')) {
      setReauthorizeDialog({
        open: true,
        fpCode: connection.fp_code,
        franchiseName: connection.franchise_name,
        tenantName: connection.connection_id,
        loading: false
      })
    }
  }

  // Handle Xero reauthorization confirm
  const handleReauthorizeConfirm = async () => {
    setReauthorizeDialog(prev => ({ ...prev, loading: true }))

    try {
      const response = await apiClient.post(`/connections/xero/reauthorize/${reauthorizeDialog.fpCode}`)

      if (response.data?.auth_url) {
        const popup = window.open(response.data.auth_url, 'xero-reauth', 'width=600,height=700')

        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed)
            setReauthorizeDialog({ open: false, fpCode: '', franchiseName: '', loading: false })
            fetchConnections()
          }
        }, 1000)
      }
    } catch (error) {
      console.error('Failed to reauthorize Xero:', error)
      setReauthorizeDialog(prev => ({ ...prev, loading: false }))
    }
  }

  // Handle Xero reauthorization cancel
  const handleReauthorizeCancel = () => {
    setReauthorizeDialog({ open: false, fpCode: '', franchiseName: '', loading: false })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'error':
        return 'error'
      case 'reauthorization_required':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon fontSize="small" />
      case 'error':
        return <ErrorIcon fontSize="small" />
      case 'reauthorization_required':
        return <WarningIcon fontSize="small" />
      default:
        return <WarningIcon fontSize="small" />
    }
  }

  const formatStatus = (status: string) => {
    switch (status) {
      case 'reauthorization_required':
        return 'Re-auth Required'
      default:
        return status.charAt(0).toUpperCase() + status.slice(1)
    }
  }

  // Define table columns
  const columns: TableColumn<SystemConnection>[] = [
    {
      key: 'system_name',
      label: 'System',
      sortable: true,
      render: (connection) => {
        const isClickable = (connection.system_name === 'xero' && connection.metadata?.short_code) ||
          (connection.system_name === 'lightspeed' && (connection.metadata?.store_code || connection.connection_id))
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: isClickable ? 'pointer' : 'default',
              '&:hover': {
                color: isClickable ? poolwerxColors.teal : 'inherit',
                opacity: isClickable ? 0.8 : 1
              }
            }}
            onClick={() => isClickable && handleSystemMainAppClick(connection)}
          >
            {getSystemIcon(connection.system_name, { size: 24 })}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {connection.system_name.charAt(0).toUpperCase() + connection.system_name.slice(1)}
            </Typography>
          </Box>
        )
      }
    },
    {
      key: 'franchise_name',
      label: 'Franchise Name',
      sortable: true,
      render: (connection) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {connection.country && (
            <Chip
              label={connection.country === 'AU' ? '🇦🇺' : connection.country === 'NZ' ? '🇳🇿' : connection.country === 'US' ? '🇺🇸' : '--'}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: connection.country === 'AU' ? '#E3F2FD' :
                              connection.country === 'NZ' ? '#F3E5F5' :
                              connection.country === 'US' ? '#FFEBEE' :
                              'transparent',
                borderColor: connection.country === 'AU' ? '#90CAF9' :
                            connection.country === 'NZ' ? '#E1BEE7' :
                            connection.country === 'US' ? '#FFCDD2' :
                            undefined,
                '& .MuiChip-label': { fontWeight: 500 }
              }}
            />
          )}
          <Link
            component={RouterLink}
            to={generateAdminFpUrl(connection.fp_id, 'edit')}
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 800,
              '&:hover': { color: poolwerxColors.teal }
            }}
          >
            {connection.franchise_name}
          </Link>
        </Box>
      )
    },
    {
      key: 'connection_id',
      label: 'Connection ID',
      render: (connection) => (
        <Typography variant="body2" sx={{
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          maxWidth: 150,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {connection.connection_id || '-'}
        </Typography>
      )
    },
    {
      key: 'error_message',
      label: 'Error Message',
      render: (connection) => (
        <Typography variant="body2" color="error" sx={{
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {connection.error_message || '-'}
        </Typography>
      )
    },
    {
      key: 'last_tested_at',
      label: 'Last Tested',
      sortable: true,
      render: (connection) => (
        <Typography variant="body2" color="text.secondary">
          {connection.last_tested_at ? formatToLongDate(connection.last_tested_at) : 'Never'}
        </Typography>
      )
    },
    {
      key: 'last_sync_at',
      label: 'Last Sync',
      sortable: true,
      render: (connection) => (
        <Typography variant="body2" color="text.secondary">
          {connection.last_sync_at ? formatToLongDate(connection.last_sync_at) : 'Never'}
        </Typography>
      )
    },
    {
      key: 'connection_status',
      label: 'Status',
      sortable: true,
      align: 'center',
      render: (connection) => {
        const isXeroError = connection.system_name === 'xero' &&
          (connection.connection_status === 'error' || connection.connection_status === 'reauthorization_required')
        return (
          <Chip
            label={formatStatus(connection.connection_status)}
            color={getStatusColor(connection.connection_status) as any}
            variant="outlined"
            size="small"
            icon={getStatusIcon(connection.connection_status)}
            onClick={isXeroError ? () => handleStatusClick(connection) : undefined}
            sx={{
              minWidth: 120,
              cursor: isXeroError ? 'pointer' : 'default',
              '&:hover': isXeroError ? {
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                transform: 'scale(1.02)'
              } : {}
            }}
          />
        )
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      render: (connection) => {
        const hasIntegrationLink = (connection.system_name === 'xero' && connection.metadata?.short_code) ||
          (connection.system_name === 'lightspeed' && (connection.metadata?.store_code || connection.connection_id))
        return (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Tooltip title={`Manage ${connection.system_name} connection for ${connection.fp_code}`}>
              <IconButton
                size="small"
                onClick={() => handleManageConnection(connection)}
                sx={{
                  color: poolwerxColors.teal,
                  '&:hover': { backgroundColor: 'rgba(0, 127, 152, 0.1)' }
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {hasIntegrationLink && (
              <Tooltip title="System Integrations">
                <IconButton
                  size="medium"
                  onClick={() => handleSystemIntegrationClick(connection)}
                  sx={{
                    color: poolwerxColors.teal,
                    padding: '6px',
                    '&:hover': { backgroundColor: 'rgba(0, 127, 152, 0.1)' }
                  }}
                >
                  <LinkIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      }
    }
  ]

  return (
    <>
      <AdminPageHeader pageKey="systemConnections" />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {!error && successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <ReportFilterCard
        filters={sharedFilters}
        enabledFilters={ENABLED_FILTERS}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        loading={loading}
      />

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Connections Table */}
      {!loading && (
        <BaseDataTable
          columns={columns}
          data={paginatedConnections}
          variant="enhanced"
          title="System Connections"
          titleIcon={<LinkIcon />}
          showCount
          totalCount={filteredAndSortedConnections.length}
          countLabel="connections"
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          pagination={{
            page,
            perPage: rowsPerPage,
            total: filteredAndSortedConnections.length,
            onPageChange: handlePageChange,
            showFirstLast: true,
            size: 'large'
          }}
          emptyMessage="No system connections match your current filters."
          emptyComponent={
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No system connections match your current filters.
              </Typography>
              <Button
                variant="outlined"
                onClick={clearFilters}
                sx={{
                  mt: 2,
                  borderColor: poolwerxColors.red,
                  color: poolwerxColors.red,
                  '&:hover': {
                    borderColor: poolwerxColors.redHover,
                    backgroundColor: poolwerxColors.redLight,
                    color: poolwerxColors.redHover
                  }
                }}
              >
                Clear All Filters
              </Button>
            </Box>
          }
        />
      )}

      {/* Xero Reauthorization Dialog */}
      <XeroReauthorizeDialog
        open={reauthorizeDialog.open}
        onClose={handleReauthorizeCancel}
        onConfirm={handleReauthorizeConfirm}
        fpCode={reauthorizeDialog.fpCode}
        franchiseName={reauthorizeDialog.franchiseName}
        tenantName={reauthorizeDialog.tenantName}
        loading={reauthorizeDialog.loading}
      />
    </>
  )
}

export default SystemConnections
